import React, { useState } from 'react';
import { Box, Button, Container, Typography, Paper, LinearProgress, Chip, Switch, FormControlLabel, Alert } from '@mui/material';
import { WalletIcon, RefreshCcwIcon, ClockIcon, AlertTriangleIcon } from 'lucide-react';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
interface PartnerBalanceProps {
  onLogout: () => void;
}
const LOW_BALANCE_THRESHOLD = 50000;
export const PartnerBalance: React.FC<PartnerBalanceProps> = ({
  onLogout
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [alertMode, setAlertMode] = useState(false);
  const balanceData = {
    currentBalance: 50000,
    currency: 'PKR',
    allocated: 5000000
  };
  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => {
      setLastUpdated(new Date());
      setIsRefreshing(false);
    }, 1200);
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-PK', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };
  const isLow = balanceData.currentBalance <= LOW_BALANCE_THRESHOLD;
  const showRed = alertMode && isLow;
  const usagePercent = Math.min((balanceData.allocated - balanceData.currentBalance) / balanceData.allocated * 100, 100);
  return <Box sx={{
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: '#F8FAFC'
  }} data-id="element-1698">
      <TopBar onMenuClick={handleDrawerToggle} onLogout={onLogout} data-id="element-1699" />
      <Sidebar open={mobileOpen} onClose={handleDrawerToggle} activePage="partner-balance" data-id="element-1700" />
      <Box component="main" sx={{
      flexGrow: 1,
      p: 3,
      width: {
        sm: 'calc(100% - 240px)'
      },
      ml: 0,
      mt: 8,
      position: 'relative'
    }} data-id="element-1701">
        {isRefreshing && <LinearProgress sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        zIndex: 9999,
        backgroundColor: '#E5E7EB',
        '& .MuiLinearProgress-bar': {
          backgroundColor: '#0052CC'
        }
      }} data-id="element-1702" />}
        <Container maxWidth="sm" data-id="element-1703">
          {/* Page Header */}
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 4
        }} data-id="element-1704">
            <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }} data-id="element-1705">
              <Box sx={{
              width: 42,
              height: 42,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#0052CC'
            }} data-id="element-1706">
                <WalletIcon size={22} color="white" data-id="element-1707" />
              </Box>
              <Typography variant="h5" fontWeight={700} color="#1F2937" data-id="element-1708">
                Partner Balance
              </Typography>
            </Box>

            <FormControlLabel control={<Switch checked={alertMode} onChange={e => setAlertMode(e.target.checked)} sx={{
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: '#EF4444'
            },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: '#EF4444'
            }
          }} data-id="element-1710" />} label={<Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5
          }} data-id="element-1711">
                  <AlertTriangleIcon size={16} color={alertMode ? '#EF4444' : '#94A3B8'} data-id="element-1712" />
                  <Typography variant="body2" sx={{
              fontWeight: 500,
              color: alertMode ? '#EF4444' : '#64748B'
            }} data-id="element-1713">
                    Alert View
                  </Typography>
                </Box>} data-id="element-1709" />
          </Box>

          {/* Alert Banner - only when alert mode ON and balance is low */}
          {alertMode && isLow && <Alert severity="error" icon={<AlertTriangleIcon size={20} data-id="element-1715" />} sx={{
          mb: 3,
          borderRadius: 2,
          border: '1px solid #FECACA',
          bgcolor: '#FEF2F2',
          '& .MuiAlert-message': {
            fontWeight: 500
          }
        }} data-id="element-1714">
              <Typography variant="body2" sx={{
            fontWeight: 600
          }} data-id="element-1716">
                Funds are near consumption! Balance is below PKR{' '}
                {LOW_BALANCE_THRESHOLD.toLocaleString()}.
              </Typography>
              <Typography variant="caption" color="text.secondary" data-id="element-1717">
                Please arrange fund replenishment immediately to avoid
                transaction failures.
              </Typography>
            </Alert>}

          {alertMode && !isLow && <Alert severity="success" sx={{
          mb: 3,
          borderRadius: 2
        }} data-id="element-1718">
              Balance is within safe limits. No alerts at this time.
            </Alert>}

          {/* Balance Card */}
          <Paper sx={{
          borderRadius: 2,
          border: showRed ? '2px solid #AE2A19' : '1px solid #E5E7EB',
          boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
          overflow: 'hidden',
          mb: 3,
          transition: 'none'
        }} data-id="element-1719">
            <Box sx={{
            backgroundColor: showRed ? '#AE2A19' : '#0052CC',
            p: 5,
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            transition: 'none'
          }} data-id="element-1720">
              {showRed && <Box sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              bgcolor: 'rgba(255,255,255,0.2)',
              borderRadius: '20px',
              px: 1.5,
              py: 0.5
            }} data-id="element-1721">
                  <AlertTriangleIcon size={14} color="white" data-id="element-1722" />
                  <Typography variant="caption" sx={{
                color: 'white',
                fontWeight: 700,
                fontSize: '0.65rem',
                letterSpacing: 1
              }} data-id="element-1723">
                    LOW BALANCE
                  </Typography>
                </Box>}

              <Typography variant="overline" sx={{
              color: 'rgba(255,255,255,0.8)',
              letterSpacing: 2,
              fontWeight: 600,
              mb: 1,
              display: 'block'
            }} data-id="element-1724">
                Current Available Balance
              </Typography>
              <Typography variant="h2" sx={{
              color: 'white',
              fontWeight: 700,
              fontSize: {
                xs: '2.5rem',
                sm: '3.5rem'
              },
              textShadow: '0 2px 10px rgba(0,0,0,0.1)',
              mb: 1
            }} data-id="element-1725">
                {formatCurrency(balanceData.currentBalance)}
              </Typography>
              <Chip label={balanceData.currency} sx={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: 'white',
              fontWeight: 600,
              fontSize: '0.85rem',
              px: 1
            }} data-id="element-1726" />
              <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              mt: 2
            }} data-id="element-1727">
                <ClockIcon size={14} color="rgba(255,255,255,0.7)" data-id="element-1728" />
                <Typography variant="caption" sx={{
                color: 'rgba(255,255,255,0.7)',
                fontSize: '0.75rem'
              }} data-id="element-1729">
                  Last Updated:{' '}
                  {lastUpdated.toLocaleString('en-PK', {
                  dateStyle: 'medium',
                  timeStyle: 'medium'
                })}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Refresh Button */}
          <Box sx={{
          display: 'flex',
          justifyContent: 'center'
        }} data-id="element-1730">
            <Button variant="contained" startIcon={<RefreshCcwIcon size={18} data-id="element-1732" />} onClick={handleRefresh} disabled={isRefreshing} sx={{
            px: 4,
            py: 1.5,
            fontSize: '0.875rem',
            borderRadius: '6px',
            backgroundColor: showRed ? '#AE2A19' : '#0052CC',
            boxShadow: 'none',
            transition: 'none',
            '&:hover': {
              backgroundColor: showRed ? '#8B1D0F' : '#003DA8',
              boxShadow: 'none'
            },
            '&.Mui-disabled': {
              backgroundColor: '#D1D5DB',
              color: '#9CA3AF'
            }
          }} data-id="element-1731">
              {isRefreshing ? 'Refreshing...' : 'Refresh Balance'}
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>;
};
export default PartnerBalance;